import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { theme } from '../styles/theme';
import { Container } from '../components/common/Container';
import { Button } from '../components/common/Button';

const ContactWrapper = styled.div`
  padding: ${theme.spacing['4xl']} 0;
  min-height: 80vh;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: ${theme.spacing['3xl']};
`;

const Title = styled.h1`
  font-size: ${theme.fontSizes['4xl']};
  margin-bottom: ${theme.spacing.lg};
  color: ${theme.colors.text.primary};
`;

const Subtitle = styled.p`
  font-size: ${theme.fontSizes.xl};
  color: ${theme.colors.text.secondary};
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;
`;

const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${theme.spacing['3xl']};
  max-width: 1200px;
  margin: 0 auto;
  
  @media (min-width: ${theme.breakpoints.md}) {
    grid-template-columns: 1fr 1fr;
  }
`;

const ContactForm = styled.form`
  background-color: ${theme.colors.background};
  border: 1px solid ${theme.colors.borderLight};
  border-radius: ${theme.radii.xl};
  padding: ${theme.spacing['2xl']};
`;

const FormGroup = styled.div`
  margin-bottom: ${theme.spacing.lg};
`;

const Label = styled.label`
  display: block;
  font-size: ${theme.fontSizes.sm};
  font-weight: 500;
  color: ${theme.colors.text.primary};
  margin-bottom: ${theme.spacing.sm};
`;

const Input = styled.input`
  width: 100%;
  padding: ${theme.spacing.md};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.md};
  font-size: ${theme.fontSizes.base};
  transition: border-color ${theme.transitions.fast};
  
  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
  }
`;

const Select = styled.select`
  width: 100%;
  padding: ${theme.spacing.md};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.md};
  font-size: ${theme.fontSizes.base};
  background-color: white;
  transition: border-color ${theme.transitions.fast};
  
  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  padding: ${theme.spacing.md};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.md};
  font-size: ${theme.fontSizes.base};
  min-height: 150px;
  resize: vertical;
  transition: border-color ${theme.transitions.fast};
  font-family: inherit;
  
  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
  }
`;

// Honeypot field - hidden from users but visible to bots
const HoneypotField = styled.input`
  position: absolute;
  left: -9999px;
  width: 1px;
  height: 1px;
  opacity: 0;
  pointer-events: none;
  tab-index: -1;
`;

const ContactInfo = styled.div`
  background-color: ${theme.colors.backgroundSecondary};
  border-radius: ${theme.radii.xl};
  padding: ${theme.spacing['2xl']};
  height: fit-content;
`;

const InfoSection = styled.div`
  margin-bottom: ${theme.spacing.xl};
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const InfoTitle = styled.h3`
  font-size: ${theme.fontSizes.lg};
  margin-bottom: ${theme.spacing.md};
  color: ${theme.colors.text.primary};
`;

const InfoContent = styled.p`
  color: ${theme.colors.text.secondary};
  line-height: 1.6;
`;

const ContactMethod = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};
  padding: ${theme.spacing.md} 0;
  
  &:not(:last-child) {
    border-bottom: 1px solid ${theme.colors.borderLight};
  }
`;

const ContactIcon = styled.div`
  width: 40px;
  height: 40px;
  background-color: ${theme.colors.primaryLight};
  border-radius: ${theme.radii.full};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${theme.fontSizes.lg};
`;

const ContactDetails = styled.div`
  flex: 1;
`;

const ContactLabel = styled.p`
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.text.muted};
  margin-bottom: ${theme.spacing.xs};
`;

const ContactValue = styled.p`
  color: ${theme.colors.text.primary};
  font-weight: 500;
`;

const SuccessMessage = styled.div`
  background-color: ${theme.colors.success}20;
  border: 1px solid ${theme.colors.success};
  border-radius: ${theme.radii.md};
  padding: ${theme.spacing.md};
  color: ${theme.colors.success};
  margin-bottom: ${theme.spacing.lg};
`;

const ErrorMessage = styled.div`
  background-color: ${theme.colors.error}20;
  border: 1px solid ${theme.colors.error};
  border-radius: ${theme.radii.md};
  padding: ${theme.spacing.md};
  color: ${theme.colors.error};
  margin-bottom: ${theme.spacing.lg};
`;

const SimpleChallenge = styled.div`
  margin-bottom: ${theme.spacing.lg};
  padding: ${theme.spacing.md};
  background-color: ${theme.colors.backgroundSecondary};
  border-radius: ${theme.radii.md};
`;

const ChallengeLabel = styled.label`
  display: block;
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.text.primary};
  margin-bottom: ${theme.spacing.sm};
`;

const ChallengeInput = styled.input`
  width: 100px;
  padding: ${theme.spacing.sm};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.sm};
  font-size: ${theme.fontSizes.base};
  
  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
  }
`;

export const ContactPage: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: 'general',
    message: '',
    website: '', // Honeypot field
    challenge: '' // Simple math challenge
  });
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitTime, setSubmitTime] = useState(0);
  const [challengeQuestion, setChalllengeQuestion] = useState({ a: 0, b: 0 });

  // Generate random simple math question
  useEffect(() => {
    const a = Math.floor(Math.random() * 10) + 1;
    const b = Math.floor(Math.random() * 10) + 1;
    setChalllengeQuestion({ a, b });
  }, []);

  // Track time on page (spam bots submit too quickly)
  useEffect(() => {
    setSubmitTime(Date.now());
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const validateForm = (): boolean => {
    // Check honeypot (should be empty)
    if (formData.website) {
      console.log('Honeypot triggered');
      return false;
    }

    // Check math challenge
    const expectedAnswer = challengeQuestion.a + challengeQuestion.b;
    if (parseInt(formData.challenge) !== expectedAnswer) {
      setError('Please answer the math question correctly.');
      return false;
    }

    // Check if form was submitted too quickly (less than 3 seconds)
    const timeOnPage = (Date.now() - submitTime) / 1000;
    if (timeOnPage < 3) {
      setError('Please take a moment to fill out the form completely.');
      return false;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Please enter a valid email address.');
      return false;
    }

    // Check for suspicious patterns in message
    const suspiciousPatterns = [
      /viagra/i,
      /casino/i,
      /lottery/i,
      /click here/i,
      /buy now/i,
      /limited time/i,
      /act now/i,
      /(http|https):\/\/[^\s]+/i // URLs in message
    ];
    
    if (suspiciousPatterns.some(pattern => pattern.test(formData.message))) {
      setError('Your message contains content that looks like spam. Please revise and try again.');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    
    if (!validateForm()) {
      // If validation fails due to spam, still show success to confuse bots
      if (formData.website) {
        setSubmitted(true);
        setTimeout(() => setSubmitted(false), 5000);
      }
      return;
    }

    setIsSubmitting(true);

    try {
      // Here you would normally send the form data to your backend
      // The backend should also implement rate limiting and additional validation
      
      // For now, just simulate a submission
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      console.log('Form submitted:', {
        name: formData.name,
        email: formData.email,
        subject: formData.subject,
        message: formData.message,
        // Don't send honeypot or challenge fields
      });
      
      setSubmitted(true);
      setFormData({
        name: '',
        email: '',
        subject: 'general',
        message: '',
        website: '',
        challenge: ''
      });
      
      // Generate new challenge
      const a = Math.floor(Math.random() * 10) + 1;
      const b = Math.floor(Math.random() * 10) + 1;
      setChalllengeQuestion({ a, b });
      
      setTimeout(() => setSubmitted(false), 5000);
    } catch (err) {
      setError('Failed to send message. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ContactWrapper>
      <Container>
        <Header>
          <Title>Get in Touch</Title>
          <Subtitle>
            Have a question, feedback, or need support? We'd love to hear from you.
          </Subtitle>
        </Header>

        <ContactGrid>
          <ContactForm onSubmit={handleSubmit}>
            {submitted && (
              <SuccessMessage>
                Thank you for your message! We'll get back to you within 24-48 hours.
              </SuccessMessage>
            )}
            
            {error && (
              <ErrorMessage>
                {error}
              </ErrorMessage>
            )}
            
            <FormGroup>
              <Label htmlFor="name">Your Name *</Label>
              <Input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                minLength={2}
                maxLength={100}
              />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="email">Email Address *</Label>
              <Input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                maxLength={255}
              />
            </FormGroup>

            {/* Honeypot field - hidden from real users */}
            <HoneypotField
              type="text"
              name="website"
              value={formData.website}
              onChange={handleChange}
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
            />

            <FormGroup>
              <Label htmlFor="subject">Subject</Label>
              <Select
                id="subject"
                name="subject"
                value={formData.subject}
                onChange={handleChange}
              >
                <option value="general">General Inquiry</option>
                <option value="support">Technical Support</option>
                <option value="feature">Feature Request</option>
                <option value="bug">Bug Report</option>
                <option value="partnership">Partnership</option>
                <option value="other">Other</option>
              </Select>
            </FormGroup>

            <FormGroup>
              <Label htmlFor="message">Message *</Label>
              <Textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                placeholder="Tell us how we can help..."
                required
                minLength={10}
                maxLength={5000}
              />
            </FormGroup>

            <SimpleChallenge>
              <ChallengeLabel htmlFor="challenge">
                Please answer: What is {challengeQuestion.a} + {challengeQuestion.b}? *
              </ChallengeLabel>
              <ChallengeInput
                type="text"
                id="challenge"
                name="challenge"
                value={formData.challenge}
                onChange={handleChange}
                required
                autoComplete="off"
              />
            </SimpleChallenge>

            <Button 
              type="submit" 
              fullWidth 
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Sending...' : 'Send Message'}
            </Button>
          </ContactForm>

          <ContactInfo>
            <InfoSection>
              <InfoTitle>Contact Information</InfoTitle>
              <InfoContent>
                Promptasaurus is a Gruntwork.ai project. We're committed to building
                AI-first solutions that make a real difference in how people interact
                with language models.
              </InfoContent>
            </InfoSection>

            <InfoSection>
              <InfoTitle>Get Support</InfoTitle>
              <ContactMethod>
                <ContactIcon>📧</ContactIcon>
                <ContactDetails>
                  <ContactLabel>Email</ContactLabel>
                  <ContactValue>support@gruntwork.ai</ContactValue>
                </ContactDetails>
              </ContactMethod>

              <ContactMethod>
                <ContactIcon>💬</ContactIcon>
                <ContactDetails>
                  <ContactLabel>GitHub Discussions</ContactLabel>
                  <ContactValue>github.com/gruntwork/promptasaurus</ContactValue>
                </ContactDetails>
              </ContactMethod>

              <ContactMethod>
                <ContactIcon>🐛</ContactIcon>
                <ContactDetails>
                  <ContactLabel>Report Issues</ContactLabel>
                  <ContactValue>GitHub Issues</ContactValue>
                </ContactDetails>
              </ContactMethod>
            </InfoSection>

            <InfoSection>
              <InfoTitle>Response Time</InfoTitle>
              <InfoContent>
                Our team typically responds within 24-48 hours during business days.
                For urgent technical issues, please use GitHub Issues with the "urgent" label.
              </InfoContent>
            </InfoSection>
          </ContactInfo>
        </ContactGrid>
      </Container>
    </ContactWrapper>
  );
};